import React, {useEffect, useState} from 'react';
import {connect} from 'react-redux';
import {useTranslation} from 'react-i18next';
import {withStyles, Grid, TextField, Button, CircularProgress} from '@material-ui/core';
import {getProfile} from '../../../../../redux/actions/User/Profile/profileAction';
import {
    bookingPhotoSearch,
    bookingPhotoSave,
    clearBookingPhoto,
} from '../../../../../redux/actions/General/BookingImport/bookingImportActions';
import Dialog from '@material-ui/core/Dialog';
import MuiDialogTitle from '@material-ui/core/DialogTitle/DialogTitle';
import MuiDialogContent from '@material-ui/core/DialogContent/DialogContent';
import MuiDialogActions from '@material-ui/core/DialogActions';
import Typography from '@material-ui/core/Typography';
import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';
import {Alert} from '@material-ui/lab';
import ButtonAtm from "../../../../atomic/Button/Button";

const titleStyles = theme => ({
    root: {
        margin: 0,
        padding: theme.spacing(2),
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
});

const DialogTitle = withStyles(titleStyles)(props => {
    const {children, classes, onClose, ...other} = props;
    return (
        <MuiDialogTitle disableTypography className={classes.root} {...other}>
            <Typography variant="h6">{children}</Typography>
            {onClose ? (
                <IconButton aria-label="close" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon className="close-button"/>
                </IconButton>
            ) : null}
        </MuiDialogTitle>
    );
});

const DialogContent = withStyles(theme => ({
    root: {padding: theme.spacing(2)},
}))(MuiDialogContent);

const DialogActions = withStyles(theme => ({
    root: {margin: 0, padding: theme.spacing(3, 3)},
}))(MuiDialogActions);

function BookingPhotoImport({
    onImport,
    userEmail,
    getProfile,
    bookingPhotoSearch,
    bookingPhotoSave,
    clearBookingPhoto,
    photoSearchFetching,
    photoSearchData,
    photoSearchError,
    photoSaveFetching,
    photoSaveData,
    photoSaveError,
}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [bookingId, setBookingId] = useState('');
    const [selected, setSelected] = useState([]);
    const [validationError, setValidationError] = useState('');

    useEffect(() => {
        if (!userEmail && typeof getProfile === 'function') {
            getProfile();
        }
    }, [userEmail, getProfile]);

    const isBusy = photoSearchFetching || photoSaveFetching;
    const photos = (photoSearchData && photoSearchData.status === 200
        && Array.isArray(photoSearchData.data)) ? photoSearchData.data : [];

    const reset = () => {
        setBookingId('');
        setSelected([]);
        setValidationError('');
        clearBookingPhoto();
    };

    const handleOpen = () => setOpen(true);

    const handleClose = () => {
        if (isBusy) return;
        setOpen(false);
        reset();
    };

    const handleFetch = () => {
        if (!bookingId.trim()) {
            setValidationError("Lütfen geçerli bir Booking.com Hotel ID giriniz.");
            return;
        }
        setValidationError('');
        setSelected([]);
        clearBookingPhoto();
        bookingPhotoSearch({hotel_id: bookingId.trim()});
    };

    const togglePhoto = (photoId) => {
        setSelected(prev =>
            prev.includes(photoId) ? prev.filter(p => p !== photoId) : [...prev, photoId]
        );
    };

    const toggleSelectAll = () => {
        if (!photos.length) return;
        if (selected.length === photos.length) {
            setSelected([]);
        } else {
            setSelected(photos.map(p => p.photo_id));
        }
    };

    const handleImport = () => {
        if (!selected.length) return;
        const selectedPhotos = photos
            .filter(p => selected.includes(p.photo_id))
            .map(p => ({
                photo_id: p.photo_id,
                url: p.url,
                caption: p.caption,
                order: p.order,
            }));
        bookingPhotoSave({
            booking_hotel_id: bookingId.trim(),
            selected_photos: selectedPhotos,
        });
    };

    useEffect(() => {
        if (photoSaveData && photoSaveData.status === 200
            && photoSaveData.data && Array.isArray(photoSaveData.data.saved)
            && photoSaveData.data.saved.length > 0
            && typeof onImport === 'function') {
            onImport(photoSaveData.data.saved);
            setOpen(false);
            reset();
        }
    }, [photoSaveData]);

    const searchErrorMessage = (photoSearchData && photoSearchData.error && photoSearchData.message)
        || (photoSearchError && photoSearchError.response && photoSearchError.response.data && photoSearchError.response.data.message)
        || (photoSearchError && 'Fotoğraflar getirilirken bir hata oluştu.');

    const saveErrorMessage = (photoSaveData && photoSaveData.error && photoSaveData.message)
        || (photoSaveError && photoSaveError.response && photoSaveError.response.data && photoSaveError.response.data.message)
        || (photoSaveError && 'Fotoğraflar kaydedilirken bir hata oluştu.');

    return (
        <>
            <ButtonAtm
                className="m-auto room-rate-modal-save bg-red"
                buttontext={"Booking.com'dan İçe Aktar"}
                color="primary"
                startIcon={<CloudDownloadIcon/>}
                onClick={handleOpen}
                type="submit"
                style={{padding: '1rem'}}
            />
            <Dialog
                onClose={handleClose}
                open={open}
                maxWidth="md"
                fullWidth
                disableBackdropClick={isBusy}
                disableEscapeKeyDown={isBusy}
                aria-labelledby="booking-import-title"
            >
                <DialogTitle className="red-bottom-border" id="booking-import-title" onClose={isBusy ? undefined : handleClose}>
                    Booking.com'dan Fotoğraf İçe Aktar
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" style={{color: '#5b6675', marginBottom: 16}}>
                        Otelin Booking.com ID bilgisini giriniz. Bilgileri getirdikten sonra içe
                        aktarmak istediğiniz fotoğrafları seçebilirsiniz.
                    </Typography>

                    <div style={{
                        display: 'flex',
                        gap: 12,
                        alignItems: 'flex-end',
                        flexWrap: 'wrap',
                        marginBottom: 16,
                    }}>
                        <div style={{flex: '1 1 240px', minWidth: 200}}>
                            <TextField
                                fullWidth
                                variant="outlined"
                                size="small"
                                label="Booking.com Hotel ID"
                                placeholder="örn: 1234567"
                                value={bookingId}
                                onChange={e => setBookingId(e.target.value)}
                                disabled={isBusy}
                                onKeyDown={e => {
                                    if (e.key === 'Enter') {
                                        e.preventDefault();
                                        handleFetch();
                                    }
                                }}
                            />
                        </div>
                        <Button
                            variant="contained"
                            color="primary"
                            className="get-info-btn"
                            onClick={handleFetch}
                            disabled={isBusy || !bookingId.trim()}
                            style={{
                                height: 40,
                                minWidth: 160,
                                textTransform: 'none',
                                fontWeight: 600,
                            }}
                        >
                            {photoSearchFetching
                                ? <CircularProgress size={20} color="inherit"/>
                                : 'Fotoğrafları Getir'}
                        </Button>
                    </div>

                    {validationError && (
                        <Alert severity="error" style={{marginBottom: 16}}>{validationError}</Alert>
                    )}

                    {!validationError && (photoSearchError || (photoSearchData && photoSearchData.error)) && (
                        <Alert severity="error" style={{marginBottom: 16}}>{searchErrorMessage}</Alert>
                    )}

                    {(photoSaveError || (photoSaveData && photoSaveData.error)) && (
                        <Alert severity="error" style={{marginBottom: 16}}>{saveErrorMessage}</Alert>
                    )}

                    {photoSearchData && photoSearchData.status === 200 && photos.length === 0 && (
                        <Alert severity="info" style={{marginBottom: 16}}>
                            Bu otele ait fotoğraf bulunamadı.
                        </Alert>
                    )}

                    {photos.length > 0 && (
                        <>
                            <div style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                marginBottom: 12,
                                marginTop: 20
                            }}>
                                <Typography variant="body2" style={{fontWeight: 600}}>
                                    {selected.length} / {photos.length} fotoğraf seçildi
                                </Typography>
                                <Button
                                    size="small"
                                    className='room-rate-modal-save bg-red modal-select-all-btn'
                                    onClick={toggleSelectAll}
                                    disabled={isBusy}
                                    style={{textTransform: 'none'}}
                                >
                                    {selected.length === photos.length ? 'Seçimi Temizle' : 'Tümünü Seç'}
                                </Button>
                            </div>
                            <Grid container spacing={2}>
                                {photos.map((photo, i) => {
                                    const isSelected = selected.includes(photo.photo_id);
                                    return (
                                        <Grid item xs={6} sm={4} md={3} key={photo.photo_id || i}>
                                            <div
                                                role="button"
                                                tabIndex={0}
                                                onClick={() => !isBusy && togglePhoto(photo.photo_id)}
                                                onKeyDown={e => {
                                                    if ((e.key === 'Enter' || e.key === ' ') && !isBusy) {
                                                        e.preventDefault();
                                                        togglePhoto(photo.photo_id);
                                                    }
                                                }}
                                                style={{
                                                    position: 'relative',
                                                    cursor: isBusy ? 'default' : 'pointer',
                                                    border: isSelected
                                                        ? '3px solid #003580'
                                                        : '3px solid transparent',
                                                    borderRadius: 8,
                                                    overflow: 'hidden',
                                                    outline: 'none',
                                                    transition: 'border-color 0.2s ease',
                                                    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
                                                }}
                                            >
                                                <img
                                                    src={photo.url}
                                                    alt={photo.caption || `booking-${i}`}
                                                    style={{
                                                        width: '100%',
                                                        height: 140,
                                                        objectFit: 'cover',
                                                        display: 'block',
                                                    }}
                                                />
                                                {isSelected && (
                                                    <div style={{
                                                        position: 'absolute',
                                                        inset: 0,
                                                        background: 'rgba(0, 53, 128, 0.18)',
                                                        display: 'flex',
                                                        alignItems: 'flex-start',
                                                        justifyContent: 'flex-end',
                                                        padding: 6,
                                                    }}>
                                                        <CheckCircleIcon
                                                            style={{
                                                                color: '#003580',
                                                                background: '#fff',
                                                                borderRadius: '50%',
                                                            }}
                                                        />
                                                    </div>
                                                )}
                                            </div>
                                        </Grid>
                                    );
                                })}
                            </Grid>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={handleImport}
                        disabled={selected.length === 0 || isBusy}
                        variant="contained"
                        color="primary"
                        style={{textTransform: 'none', fontWeight: 600, height: 40, backgroundColor: '#003580'}}
                    >
                        {photoSaveFetching
                            ? <CircularProgress size={20} color="inherit"/>
                            : `İçe Aktar (${selected.length})`}
                    </Button>
                </DialogActions>
                <style>{`
                    .MuiButton-label {
                        font-size: 16px !important;
                    }
                `}</style>
            </Dialog>
        </>
    );
}

const mapStateToProps = (state) => ({
    userEmail: state.profileReducer && state.profileReducer.profileData
        && state.profileReducer.profileData.profile
        && state.profileReducer.profileData.profile.email,
    photoSearchFetching: state.bookingImportReducer && state.bookingImportReducer.photoSearchFetching,
    photoSearchData: state.bookingImportReducer && state.bookingImportReducer.photoSearchData,
    photoSearchError: state.bookingImportReducer && state.bookingImportReducer.photoSearchError,
    photoSaveFetching: state.bookingImportReducer && state.bookingImportReducer.photoSaveFetching,
    photoSaveData: state.bookingImportReducer && state.bookingImportReducer.photoSaveData,
    photoSaveError: state.bookingImportReducer && state.bookingImportReducer.photoSaveError,
});

const mapDispatchToProps = {
    getProfile,
    bookingPhotoSearch,
    bookingPhotoSave,
    clearBookingPhoto,
};

export default connect(mapStateToProps, mapDispatchToProps)(BookingPhotoImport);
